import type { AfterViewInit, OnDestroy, OnInit, ElementRef } from '@angular/core';
import type { UntypedFormControl } from '@angular/forms';
import type { MapItem } from '@web/widgets/map/type/map-widget.type';

import { ChangeDetectionStrategy, Component, HostListener, inject, Input, ViewChild } from '@angular/core';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs';

import type { Uuid } from '@common/types';
import { CredentialsFacade } from '@web/store/credentials/credentials.facade';
import { mapWidgetActions } from '@web/store/page-structure/widgets/widgets.actions';
import { WebBaseControlSettingsComponent } from '@web/widgets/common/abstract/web-base-control-settings.component';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { autocompleteOptions } from '@web/widgets/map/model/autocomplete.config';
import { defaultMapItem } from '@web/widgets/map/model/default-address';
import { mapOptions } from '@web/widgets/map/model/map-options';
import { mapStyles } from '@web/widgets/map/model/map-styles';
import type { MapStyle } from '@web/widgets/map/type/map-widget.type';

@Component({
    selector: 'web-map-settings',
    templateUrl: './map-settings.component.html',
    styleUrls: ['./map-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, GoogleMapsModule],
})
export class MapSettingsComponent
    extends WebBaseControlSettingsComponent<MapItem>
    implements OnInit, OnDestroy, AfterViewInit
{
    @Input() mapStyle: MapStyle;
    @Input() widgetId: Uuid;
    @ViewChild(GoogleMap, { static: false }) map: GoogleMap;
    @ViewChild('addressInput') addressInput: ElementRef<HTMLInputElement>;
    @ViewChild('titleInput') titleInput: ElementRef<HTMLInputElement>;
    @HostListener('window:keydown.enter')
    public onEnterKeyPress(): void {
        // if (this.invalidAddress) {
        //     return;
        // }
        // this.onSave();
    }
    credentialsFacade = inject(CredentialsFacade);
    store$ = inject(Store);

    autocomplete: google.maps.places.Autocomplete;
    autocompleteListener: google.maps.MapsEventListener;

    public mapCenter: google.maps.LatLngLiteral;
    public geocoder: google.maps.Geocoder;
    public mapOptions: google.maps.MapOptions = mapOptions;

    protected initForm(): void {
        this.form = this.fb.group<MapItem>({
            address: null,
            position: null,
            title: null,
            description: null,
        });
    }

    public ngOnInit(): void {
        this.setFormData();
        this.mapCenter = this.position.value || defaultMapItem.position;
        this.cachedInitValue = this.item;

        document.documentElement.style.setProperty('--panel-body-width', 'auto');
        this.store$.dispatch(mapWidgetActions.editing({ widgetId: this.widgetId, editing: true }));
    }

    public get invalidAddress(): boolean {
        return !this.position.value && this.address.dirty;
    }

    public ngAfterViewInit(): void {
        this.address.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.unsubscribe$)).subscribe(() => {
            this.position.setValue(null);
        });

        this.credentialsFacade.isMapsApiLoaded$.pipe(filter(Boolean), takeUntil(this.unsubscribe$)).subscribe(() => {
            this.autocomplete = new google.maps.places.Autocomplete(
                this.addressInput.nativeElement,
                autocompleteOptions
            );
            this.autocompleteListener = this.autocomplete.addListener('place_changed', () => {
                const place = this.autocomplete.getPlace();

                if (!place.geometry || !place.geometry.location) {
                    this.position.setValue(null);
                    return;
                }

                // this.settingsChangeEvent.emit({ address: this.form.value.address } as MapItem);
                this.position.setValue(place.geometry.location.toJSON());
                this.mapCenter = this.position.value;
                this.address.setValue(this.addressInput.nativeElement.value, { emitEvent: false });

                this.changeDetectorRef.detectChanges();
            });
        });
    }

    protected setFormData(): void {
        this.form.patchValue(this.item, { emitEvent: false });
    }

    public get address(): UntypedFormControl {
        return this.form.get('address') as UntypedFormControl;
    }

    public get position(): UntypedFormControl {
        return this.form.get('position') as UntypedFormControl;
    }

    public get title(): UntypedFormControl {
        return this.form.get('title') as UntypedFormControl;
    }

    public get description(): UntypedFormControl {
        return this.form.get('description') as UntypedFormControl;
    }

    public onSave(): void {
        this.saveEvent.emit(this.form.value);
    }

    public onCancel(): void {
        this.cancelEvent.emit();
    }

    mapInitialized(mapRef: google.maps.Map) {
        mapRef.setOptions({ styles: mapStyles[this.mapStyle] || [] });
        this.geocoder = new google.maps.Geocoder();
    }

    onMapClick(event: google.maps.MapMouseEvent | google.maps.IconMouseEvent) {
        this.position.setValue(event.latLng.toJSON());

        this.geocoder.geocode({ location: event.latLng }, (results, status) => {
            if (status === google.maps.GeocoderStatus.OK) {
                if (results[0]) {
                    this.address.setValue(results[0].formatted_address, { emitEvent: false });
                } else {
                    this.address.setValue(null);
                    this.position.setValue(null);
                }
            }
        });
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();

        if (this.autocomplete) {
            google.maps.event.removeListener(this.autocompleteListener);
            google.maps.event.clearInstanceListeners(this.autocomplete);
        }
        document.documentElement.style.removeProperty('--panel-body-width');
        this.store$.dispatch(mapWidgetActions.editing({ widgetId: this.widgetId, editing: false }));
    }
}
